import React, { useMemo } from "react";
import { useFormikContext } from "formik";
import { Grid, TextField, MenuItem } from "@mui/material";

export default function ChildStepApproveForm({ stepIndex, childStepIndex }) {
    const { values, setFieldValue, handleChange, touched, errors } = useFormikContext();

    // Nested path: steps[i].childSteps[j]
    const base = `steps[${stepIndex}].childSteps[${childStepIndex}]`;
    const actionPath = `${base}.onApproveAction`;
    const gotoPath = `${base}.onApproveGoToSequence`;
    const msgPath = `${base}.onApproveMessage`;

    const childSteps = values?.steps?.[stepIndex]?.childSteps || [];
    const currentChildStep = childSteps?.[childStepIndex];

    const approveOptions = [
        { value: "", label: "No special action" },
        { value: "gotostep", label: "Go to step" },
    ];

    // Generate options from sibling child steps
    const childStepOptions = useMemo(() => {
        return childSteps.map((s, idx) => ({
            // Using sequence if available, otherwise falling back to index + 1
            value: s.sequence || idx + 1,
            label: `${s.sequence || idx + 1}. ${s.childStepName?.trim() ? s.childStepName : "Untitled Child Step"}`,
        }));
    }, [childSteps]);

    const currentAction = currentChildStep?.onApproveAction ?? "";
    const currentGoto = currentChildStep?.onApproveGoToSequence ?? "";

    // Access nested error state
    const gotoErr =
        touched?.steps?.[stepIndex]?.childSteps?.[childStepIndex]?.onApproveGoToSequence &&
        errors?.steps?.[stepIndex]?.childSteps?.[childStepIndex]?.onApproveGoToSequence;

    return (
        <Grid container spacing={2}>
            {/* Action Selection */}
            <Grid size={{ md: 4, xs: 12 }}>
                <TextField
                    select
                    fullWidth
                    label="On Approve Action (Child)"
                    name={actionPath}
                    value={currentAction}
                    onChange={(e) => {
                        const v = e.target.value || "";
                        setFieldValue(actionPath, v);

                        // Clear sequence if action is changed from 'gotostep'
                        if (v !== "gotostep") {
                            setFieldValue(gotoPath, 0);
                        }
                    }}
                >
                    {approveOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>

            {/* Target Sequence Selection */}
            <Grid size={{ md: 4, xs: 12 }}>
                <TextField
                    select
                    fullWidth
                    disabled={currentAction !== "gotostep"}
                    label="Go to Child Sequence"
                    name={gotoPath}
                    value={currentAction === "gotostep" ? currentGoto : ""}
                    onChange={(e) => {
                        const v = e.target.value === "" ? 0 : Number(e.target.value);
                        setFieldValue(gotoPath, v);
                    }}
                    error={Boolean(gotoErr)}
                    helperText={gotoErr ? String(gotoErr) : ""}
                >
                    <MenuItem value="">Select child step</MenuItem>
                    {childStepOptions
                        // Avoid circular reference to itself
                        .filter((opt) => opt.value !== (currentChildStep?.sequence || childStepIndex + 1))
                        .map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                </TextField>
            </Grid>

            {/* Approval Message */}
            <Grid size={{ md: 4, xs: 12 }}>
                <TextField
                    fullWidth
                    multiline
                    label="Approve Message (optional)"
                    name={msgPath}
                    value={currentChildStep?.onApproveMessage ?? ""}
                    onChange={handleChange}
                    placeholder="Enter approval notes..."
                />
            </Grid>
        </Grid>
    );
}